import React, { FC, SyntheticEvent, useEffect, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  ButtonIconPositions,
  ButtonThemes,
  ButtonVariants,
  InputTextFieldVariantProps,
  LocationSuggestionOptionValue,
  StoreBannerLocationSearch as UIFoundationsSBLS,
  StoreBannerLocationSearchConfig,
  useIsMoblet
} from '@dcxwam/dcx-wam-ui-foundations'
import StoreBannerLocationSearchEditConfig, {
  StoreBannerLocationSearchProps
} from './StoreBannerLocationSearchEditConfig'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import { PageConfig } from '../../../../utils/page-config'
import { pushDataLayer } from '../../../../utils/datalayer'
import { buildDataLayerStoreSearch } from '../common/utils'
import { useUserLocation } from '../common/hooks/useUserLocation'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

const StoreBannerLocationSearch: FC<
  EditableComponentPropsType<StoreBannerLocationSearchProps>
> = withComponentRefresh((props) => {
  const [requestUserLocation, setRequestUserLocation] = useState<boolean>(true)
  const { userLocation } = useUserLocation({}, requestUserLocation)
  const isMobile = useIsMoblet()
  const {
    googleMapsAPIKey,
    storeMapPageUrl,
    desktopPlaceholderInput,
    mobilePlaceholderInput,
    mobileHelperText,
    desktopHelperText,
    locateMeButtonLabel,
    ...rest
  } = props.model
  const { lang, market } = PageConfig.getConfig() as {
    lang: string
    market: string
  }

  useEffect(() => {
    if (userLocation && !requestUserLocation && storeMapPageUrl) {
      window.location.href = `${storeMapPageUrl}?lat=${userLocation?.latitude}&lng=${userLocation?.longitude}`
      setRequestUserLocation(true)
    }
  }, [userLocation, requestUserLocation])

  const handleOnChange = async (newValue: string | object | null) => {
    if (!storeMapPageUrl || !newValue) {
      return
    }
    const loc = (newValue as LocationSuggestionOptionValue).value
    if (loc?.fetchFields) {
      await loc?.fetchFields({
        fields: ['displayName', 'location']
      })
      if (!loc?.location) {
        return
      }
      pushDataLayer(buildDataLayerStoreSearch(loc.displayName))
      window.location.href = `${storeMapPageUrl}?lat=${loc.location.lat()}&lng=${loc.location.lng()}&query=${loc.displayName}`
    }
  }
  const editableProps = useExtractEditableProps(props, rest)

  const storeBannerLocationSearchConfig: StoreBannerLocationSearchConfig = {
    placeholder: isMobile ? mobilePlaceholderInput : desktopPlaceholderInput,
    noOptionsText: '',
    helperText: isMobile ? mobileHelperText : desktopHelperText,
    variant: InputTextFieldVariantProps.Outlined,
    fullWidth: true,
    disableClearable: false,
    disableChevronIcon: true,
    onChange: (
      _event: SyntheticEvent<Element, Event>,
      newValue: string | object | null
    ) => {
      handleOnChange(newValue)
    },
    mapsConfigProps: {
      apiKey: googleMapsAPIKey ?? '',
      region: market?.toUpperCase(),
      language: lang
    },
    buttonConfig: {
      iconPosition: ButtonIconPositions.Left,
      buttonVariant: ButtonVariants.Text,
      buttonTheme: ButtonThemes.Dark,
      label: locateMeButtonLabel,
      icon: 'pmi-icon-locate',
      onClick: () => {
        setRequestUserLocation(false)
      }
    }
  }

  return (
    <EditableComponent {...editableProps}>
      {StoreBannerLocationSearchEditConfig.isEmpty(props.model) ? (
        StoreBannerLocationSearchEditConfig.emptyLabel
      ) : (
        <UIFoundationsSBLS {...storeBannerLocationSearchConfig} />
      )}
    </EditableComponent>
  )
})

const StoreBannerLocationSearchWithErrorBoundary = withErrorBoundary(
  StoreBannerLocationSearch,
  {
    componentName: 'StoreBannerLocationSearch',
    fallback: <ErrorFallback componentName="StoreBannerLocationSearch" />
  }
)

export default StoreBannerLocationSearchWithErrorBoundary
